import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { COUNTRIES, PRO, tr } from "../i18n";
import { useLang } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = "om_partner_jwt";
const getTok = () => localStorage.getItem(TKEY) || "";
const authHdr = () => ({ headers: { Authorization: `Bearer ${getTok()}` } });

const statusLabel = (lang, s) => tr(lang, PRO[`st_${String(s).replace(/\s+/g, "")}`] || { en: s });

const PoweredBy = () => {
  const { lang } = useLang();
  return (
    <p data-testid="powered-by" className="text-xs text-slate-400 mt-6">{tr(lang, PRO.powered_by)} <span className="text-[#C4A052] font-semibold">One Match</span></p>
  );
};

const Field = ({ label, ...p }) => (
  <label className="block mb-3 text-left">
    <span className="block text-sm text-slate-300 mb-1">{label}</span>
    <input {...p} className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white focus:border-[#C4A052] outline-none" />
  </label>
);

const Shell = ({ children, title }) => {
  const { lang } = useLang();
  return (
    <div className="min-h-screen bg-[#0B1524] text-white flex flex-col items-center py-14 px-4">
      <div className="w-full max-w-3xl">
        <p className="font-display text-2xl mb-1">One Match <span className="text-[#C4A052]">{tr(lang, PRO.for_professionals)}</span></p>
        {title && <h1 className="text-3xl font-bold mb-6">{title}</h1>}
        {children}
      </div>
    </div>
  );
};

function Signup() {
  const nav = useNavigate();
  const { lang } = useLang();
  const [mode, setMode] = useState("signup");
  const [f, setF] = useState({ companyName: "", contactName: "", email: "", password: "", country: "PT" });
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/b2b/signup`, f);
      localStorage.setItem(TKEY, r.data.token);
      toast.success(tr(lang, PRO.t_welcome));
      nav("/professionals/onboarding");
    } catch (e) { toast.error(e?.response?.data?.detail || tr(lang, PRO.t_signup_failed)); }
    setBusy(false);
  };
  const doLogin = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, creds);
      if (!String(r.data.role || "").startsWith("partner")) { toast.error(tr(lang, PRO.t_not_partner)); setBusy(false); return; }
      localStorage.setItem(TKEY, r.data.token);
      nav("/professionals/dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || tr(lang, PRO.t_login_failed)); }
    setBusy(false);
  };

  return (
    <Shell title={mode === "signup" ? tr(lang, PRO.signup_title) : tr(lang, PRO.login_title)}>
      <p className="text-slate-300 mb-6">{tr(lang, PRO.intro)}</p>
      {mode === "signup" ? (
        <div data-testid="signup-form" className="bg-[#12233F] rounded-2xl p-6 border border-slate-700">
          <Field label={tr(lang, PRO.f_company)} data-testid="signup-company" value={f.companyName} onChange={set("companyName")} />
          <Field label={tr(lang, PRO.f_contact)} data-testid="signup-contact" value={f.contactName} onChange={set("contactName")} />
          <Field label={tr(lang, PRO.f_email)} type="email" data-testid="signup-email" value={f.email} onChange={set("email")} />
          <Field label={tr(lang, PRO.f_password)} type="password" data-testid="signup-password" value={f.password} onChange={set("password")} />
          <label className="block mb-4 text-left">
            <span className="block text-sm text-slate-300 mb-1">{tr(lang, PRO.f_country)}</span>
            <select data-testid="signup-country" value={f.country} onChange={set("country")} className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-white">
              {COUNTRIES.map((c) => <option key={c.value} value={c.value}>{tr(lang, c)}</option>)}
            </select>
          </label>
          <button data-testid="signup-submit" disabled={busy} onClick={submit} className="w-full bg-[#C4A052] text-[#0B1524] font-semibold rounded-lg py-3 hover:opacity-90 disabled:opacity-50">{busy ? tr(lang, PRO.creating) : tr(lang, PRO.start_trial)}</button>
          <p className="text-sm text-slate-400 mt-4">{tr(lang, PRO.already_partner)} <button data-testid="switch-login" className="text-[#C4A052] underline" onClick={() => setMode("login")}>{tr(lang, PRO.sign_in)}</button></p>
        </div>
      ) : (
        <div data-testid="login-form" className="bg-[#12233F] rounded-2xl p-6 border border-slate-700">
          <Field label={tr(lang, PRO.f_email)} type="email" data-testid="login-email" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} />
          <Field label={tr(lang, PRO.pw_label)} type="password" data-testid="login-password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
          <button data-testid="login-submit" disabled={busy} onClick={doLogin} className="w-full bg-[#C4A052] text-[#0B1524] font-semibold rounded-lg py-3 hover:opacity-90 disabled:opacity-50">{tr(lang, PRO.sign_in)}</button>
          <p className="text-sm text-slate-400 mt-4">{tr(lang, PRO.new_here)} <button className="text-[#C4A052] underline" onClick={() => setMode("signup")}>{tr(lang, PRO.create_account)}</button></p>
        </div>
      )}
      <PoweredBy />
    </Shell>
  );
}

function Onboarding() {
  const nav = useNavigate();
  const { lang } = useLang();
  const [f, setF] = useState({ brandName: "", logoUrl: "", primaryColor: "#0B5", website: "", phone: "", contactEmail: "", notifyEmail: "", replyTo: "", bookingIntro: "", businessType: "", marketsServed: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => { if (!getTok()) nav("/professionals"); }, []);
  const save = async () => {
    try {
      await axios.put(`${API}/b2b/onboarding`, {
        branding: { brandName: f.brandName, logoUrl: f.logoUrl, primaryColor: f.primaryColor, website: f.website, contactPhone: f.phone, contactEmail: f.contactEmail },
        email: { notifyEmail: f.notifyEmail, replyTo: f.replyTo, fromName: f.brandName, fromEmail: f.contactEmail },
        booking: { introCallUrl: f.bookingIntro },
        businessType: f.businessType,
        marketsServed: f.marketsServed.split(",").map((s) => s.trim()).filter(Boolean),
      }, authHdr());
      toast.success(tr(lang, PRO.t_branding_saved));
      nav("/professionals/dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || tr(lang, PRO.t_save_failed)); }
  };
  return (
    <Shell title={tr(lang, PRO.onb_title)}>
      <div data-testid="onboarding-form" className="bg-[#12233F] rounded-2xl p-6 border border-slate-700 grid sm:grid-cols-2 gap-x-4">
        <Field label={tr(lang, PRO.onb_brand)} data-testid="onb-brand" value={f.brandName} onChange={set("brandName")} />
        <Field label={tr(lang, PRO.onb_logo)} data-testid="onb-logo" value={f.logoUrl} onChange={set("logoUrl")} />
        <Field label={tr(lang, PRO.onb_color)} type="color" data-testid="onb-color" value={f.primaryColor} onChange={set("primaryColor")} />
        <Field label={tr(lang, PRO.onb_website)} data-testid="onb-website" value={f.website} onChange={set("website")} />
        <Field label={tr(lang, PRO.onb_phone)} data-testid="onb-phone" value={f.phone} onChange={set("phone")} />
        <Field label={tr(lang, PRO.onb_contact_email)} data-testid="onb-contact-email" value={f.contactEmail} onChange={set("contactEmail")} />
        <Field label={tr(lang, PRO.onb_notify)} data-testid="onb-notify" value={f.notifyEmail} onChange={set("notifyEmail")} />
        <Field label={tr(lang, PRO.onb_replyto)} data-testid="onb-replyto" value={f.replyTo} onChange={set("replyTo")} />
        <Field label={tr(lang, PRO.onb_booking)} data-testid="onb-booking" value={f.bookingIntro} onChange={set("bookingIntro")} />
        <Field label={tr(lang, PRO.onb_biztype)} data-testid="onb-biztype" value={f.businessType} onChange={set("businessType")} />
        <Field label={tr(lang, PRO.onb_markets)} data-testid="onb-markets" value={f.marketsServed} onChange={set("marketsServed")} />
      </div>
      <button data-testid="onboarding-save" onClick={save} className="mt-5 bg-[#C4A052] text-[#0B1524] font-semibold rounded-lg py-3 px-8 hover:opacity-90">{tr(lang, PRO.onb_save)}</button>
      <PoweredBy />
    </Shell>
  );
}

const STATUSES = ["New", "Contacted", "Consultation Booked", "Qualified", "Converted", "Closed"];

function Dashboard() {
  const nav = useNavigate();
  const { lang } = useLang();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const load = async () => {
    try {
      const m = await axios.get(`${API}/b2b/me`, authHdr());
      setMe(m.data);
      const l = await axios.get(`${API}/b2b/leads`, authHdr());
      setLeads(l.data.leads);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 403) { localStorage.removeItem(TKEY); nav("/professionals"); }
      else { toast.error(e?.response?.data?.detail || tr(lang, PRO.t_load_failed)); }
    }
  };
  useEffect(() => { if (!getTok()) nav("/professionals"); else load(); }, []);
  const updateStatus = async (id, status) => {
    try { await axios.patch(`${API}/b2b/leads/${id}`, { status }, authHdr()); toast.success(tr(lang, PRO.t_updated)); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || tr(lang, PRO.t_update_failed)); }
  };
  if (!me) return <Shell title={tr(lang, PRO.loading)}><PoweredBy /></Shell>;
  const brand = me.brandName || me.name;
  const pct = Math.min(100, Math.round((me.usage.used / me.usage.limit) * 100));
  return (
    <Shell title={`${brand} — ${tr(lang, PRO.lead_dashboard)}`}>
      {!me.operational && (
        <div data-testid="subscription-banner" className="bg-amber-900/50 border border-amber-600 rounded-xl p-4 mb-5">
          <p className="font-semibold">{tr(lang, PRO.sub_required)}</p>
          <p className="text-sm text-slate-300">{tr(lang, PRO.trial_ended)} <button className="underline text-[#C4A052]" onClick={() => nav("/professionals/billing")}>{tr(lang, PRO.subscribe_to_continue)}</button>.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-[#12233F] rounded-xl p-4 border border-slate-700"><p className="text-slate-400 text-sm">{tr(lang, PRO.d_status)}</p><p data-testid="db-status" className="text-lg font-semibold capitalize">{me.subscriptionStatus}{me.trialActive ? ` (${tr(lang, PRO.trial_ends_inline)} ${new Date(me.trialEndsAt).toLocaleDateString()})` : ""}</p></div>
        <div className="bg-[#12233F] rounded-xl p-4 border border-slate-700"><p className="text-slate-400 text-sm">{tr(lang, PRO.d_assessments)}</p><p data-testid="db-usage" className="text-lg font-semibold">{me.usage.used} / {me.usage.limit}</p><div className="h-2 bg-slate-700 rounded mt-2"><div className="h-2 bg-[#C4A052] rounded" style={{ width: `${pct}%` }} /></div></div>
        <div className="bg-[#12233F] rounded-xl p-4 border border-slate-700"><p className="text-slate-400 text-sm">{tr(lang, PRO.d_plan)}</p><p className="text-lg font-semibold">{me.foundingPartner ? tr(lang, PRO.plan_founding) : tr(lang, PRO.plan_pro)}</p><button data-testid="db-billing-link" onClick={() => nav("/professionals/billing")} className="text-[#C4A052] underline text-sm">{tr(lang, PRO.manage_billing)}</button></div>
      </div>
      <div data-testid="leads-table" className="bg-[#12233F] rounded-2xl border border-slate-700 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-800 text-slate-300"><tr><th className="p-3">{tr(lang, PRO.th_name)}</th><th className="p-3">{tr(lang, PRO.th_country)}</th><th className="p-3">{tr(lang, PRO.th_assessed)}</th><th className="p-3">{tr(lang, PRO.th_top_match)}</th><th className="p-3">{tr(lang, PRO.th_status)}</th></tr></thead>
          <tbody>
            {leads.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-400">{tr(lang, PRO.no_leads)}</td></tr>}
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-slate-700">
                <td className="p-3">{l.firstName} {l.lastName}<br /><span className="text-slate-400 text-xs">{l.email}</span></td>
                <td className="p-3">{l.country}</td>
                <td className="p-3">{l.assessmentDate ? new Date(l.assessmentDate).toLocaleDateString() : "—"}</td>
                <td className="p-3">{(l.topMatches && l.topMatches[0] && l.topMatches[0].name) || "—"}</td>
                <td className="p-3">
                  <select data-testid={`lead-status-${l.id}`} disabled={!me.operational} value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="bg-slate-800 border border-slate-600 rounded px-2 py-1">
                    {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(lang, s)}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PoweredBy />
    </Shell>
  );
}

function Billing() {
  const nav = useNavigate();
  const { lang } = useLang();
  const [s, setS] = useState(null);
  const [justSubscribed, setJustSubscribed] = useState(false);
  const load = async () => {
    try { const r = await axios.get(`${API}/b2b/subscription`, authHdr()); setS(r.data); return r.data; }
    catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 403) { localStorage.removeItem(TKEY); nav("/professionals"); }
      else { toast.error(e?.response?.data?.detail || tr(lang, PRO.t_load_failed)); }
      return null;
    }
  };
  useEffect(() => {
    if (!getTok()) { nav("/professionals"); return; }
    const outcome = new URLSearchParams(window.location.search).get("checkout");
    if (outcome === "success") {
      setJustSubscribed(true);
      toast.success(tr(lang, PRO.t_sub_activated));
      let tries = 0;
      const poll = async () => {
        const d = await load();
        tries += 1;
        if (d && d.subscriptionStatus === "active") return;
        if (tries < 4) setTimeout(poll, 2000);
      };
      poll();
      window.history.replaceState({}, "", "/professionals/billing");
    } else {
      if (outcome === "cancel") { toast.message(tr(lang, PRO.t_checkout_canceled)); window.history.replaceState({}, "", "/professionals/billing"); }
      load();
    }
  }, []);
  const subscribe = async () => {
    try {
      const r = await axios.post(`${API}/b2b/subscription/checkout`, { origin_url: window.location.origin }, authHdr());
      window.location.href = r.data.checkout_url;
    } catch (e) {
      if (e?.response?.status === 501) toast.message(tr(lang, PRO.t_billing_pending), { description: tr(lang, PRO.t_billing_pending_desc) });
      else toast.error(e?.response?.data?.detail || tr(lang, PRO.t_checkout_failed));
    }
  };
  if (!s) return <Shell title={tr(lang, PRO.loading)}><PoweredBy /></Shell>;
  const price = s.foundingSlotsLeft > 0 || s.foundingPriceLocked ? 39 : 59;
  return (
    <Shell title={tr(lang, PRO.billing_title)}>
      {justSubscribed && <div data-testid="checkout-success" className="bg-emerald-900/40 border border-emerald-600 rounded-xl p-4 mb-4"><p className="font-semibold">{tr(lang, PRO.t_sub_activated)}</p></div>}
      <div data-testid="billing-card" className="bg-[#12233F] rounded-2xl p-6 border border-slate-700">
        <p className="text-slate-300">{tr(lang, PRO.current_status)} <span className="font-semibold capitalize" data-testid="billing-status">{s.subscriptionStatus}</span></p>
        {s.trialActive && <p className="text-slate-300">{tr(lang, PRO.trial_ends)} {new Date(s.trialEndsAt).toLocaleDateString()}</p>}
        <div className="my-5 flex items-end gap-2">
          <span data-testid="billing-price" className="text-4xl font-bold">€{price}</span><span className="text-slate-400 mb-1">{tr(lang, PRO.per_month)}</span>
          {(s.foundingSlotsLeft > 0 || s.foundingPriceLocked) && <span className="ml-2 bg-[#C4A052]/20 text-[#C4A052] text-xs px-2 py-1 rounded" data-testid="founding-badge">{tr(lang, PRO.founding_partner)}</span>}
        </div>
        <p className="text-sm text-slate-400 mb-1">{tr(lang, PRO.includes)}</p>
        <p className="text-sm text-slate-400 mb-5" data-testid="founding-slots">{tr(lang, PRO.founding_slots_left)} {s.foundingSlotsLeft} / {s.foundingSlotsTotal}</p>
        {s.subscriptionStatus === "active" ? (
          <button data-testid="manage-sub" onClick={async () => { try { const r = await axios.post(`${API}/b2b/subscription/portal`, { origin_url: window.location.origin }, authHdr()); window.location.href = r.data.portal_url; } catch (e) { toast.error(tr(lang, PRO.t_portal_unavailable)); } }} className="bg-slate-700 text-white rounded-lg py-3 px-8">{tr(lang, PRO.manage_sub)}</button>
        ) : (
          <button data-testid="subscribe-btn" onClick={subscribe} className="bg-[#C4A052] text-[#0B1524] font-semibold rounded-lg py-3 px-8 hover:opacity-90">{tr(lang, PRO.subscribe)} €{price}{tr(lang, PRO.per_month)}</button>
        )}
        <button onClick={() => nav("/professionals/dashboard")} className="ml-3 text-[#C4A052] underline">{tr(lang, PRO.back_to_dashboard)}</button>
      </div>
      <PoweredBy />
    </Shell>
  );
}

export default function Professionals({ view }) {
  if (view === "onboarding") return <Onboarding />;
  if (view === "dashboard") return <Dashboard />;
  if (view === "billing") return <Billing />;
  return <Signup />;
}
